import type { ProgramExercise, TargetSpec } from "./api";

/** Подписи словесной шкалы. Калибровка — в 02-spec.md §2.2. */
const INTENSITY_LABEL: Record<string, string> = {
  heavy: "тяжело",
  medium: "средне",
  light: "легко",
  drop: "дропсет",
};

/**
 * Мышечные группы в порядке ленты фильтров: сначала то, вокруг чего строят
 * тренировку (грудь/спина/ноги), потом мелкие группы. Алфавит здесь бесполезен —
 * человек ищет не по букве, а по тому, что сегодня качает.
 */
export const MUSCLE_GROUPS: { key: string; label: string }[] = [
  { key: "chest", label: "Грудь" },
  { key: "back", label: "Спина" },
  { key: "lats", label: "Широчайшие" },
  { key: "quads", label: "Квадрицепс" },
  { key: "hamstrings", label: "Бицепс бедра" },
  { key: "glutes", label: "Ягодицы" },
  { key: "delts", label: "Плечи" },
  { key: "biceps", label: "Бицепс" },
  { key: "triceps", label: "Трицепс" },
  { key: "traps", label: "Трапеции" },
  { key: "calves", label: "Икры" },
  { key: "forearms", label: "Предплечья" },
  { key: "abs", label: "Пресс" },
  { key: "other", label: "Другое" },
];

const MUSCLE_LABEL: Record<string, string> = Object.fromEntries(
  MUSCLE_GROUPS.map((m) => [m.key, m.label]),
);

/** Подпись мышцы. В API она лежит английским ключом, показывать его нельзя. */
export const muscleLabel = (key: string): string => MUSCLE_LABEL[key] ?? key;

/** Снаряды, по которым фильтруют выбор. Редкие (EZ-гриф, резинка) — только подписью. */
export const EQUIPMENT_FILTERS: { key: string; label: string }[] = [
  { key: "barbell", label: "Штанга" },
  { key: "dumbbell", label: "Гантели" },
  { key: "machine", label: "Тренажёр" },
  { key: "cable", label: "Блок" },
  { key: "bodyweight", label: "Свой вес" },
  { key: "smith", label: "Смит" },
  { key: "kettlebell", label: "Гири" },
];

const EQUIPMENT_LABEL: Record<string, string> = {
  ...Object.fromEntries(EQUIPMENT_FILTERS.map((e) => [e.key, e.label])),
  ez_bar: "EZ-гриф",
  band: "Резинка",
  other: "Другое",
};

export const equipmentLabel = (key: string): string => EQUIPMENT_LABEL[key] ?? key;

/** Все снаряды — для формы своего упражнения; фильтры показывают частые. */
export const EQUIPMENT_ALL: { key: string; label: string }[] = [
  ...EQUIPMENT_FILTERS,
  { key: "ez_bar", label: "EZ-гриф" },
  { key: "band", label: "Резинка" },
  { key: "other", label: "Другое" },
];

/** Тип движения простыми словами: ключи API человеку ничего не говорят. */
export const MOVEMENTS: { key: string; label: string }[] = [
  { key: "push", label: "Жим" },
  { key: "pull", label: "Тяга" },
  { key: "squat", label: "Присед" },
  { key: "hinge", label: "Наклон" },
  { key: "isolation", label: "Изоляция" },
  { key: "carry", label: "Переноска" },
  { key: "other", label: "Другое" },
];

const MOVEMENT_LABEL: Record<string, string> = Object.fromEntries(
  MOVEMENTS.map((m) => [m.key, m.label]),
);

export const movementLabel = (key: string): string => MOVEMENT_LABEL[key] ?? key;

/** Класс для цветовой метки: тяжёлая нагрузка должна быть видна не читая. */
export const intensityClass = (spec: TargetSpec): string =>
  spec.type === "intensity_label" ? `tag tag--${spec.value}` : "tag";

export function targetLabel(spec: TargetSpec): string {
  switch (spec.type) {
    case "intensity_label":
      return INTENSITY_LABEL[spec.value] ?? spec.value;
    case "percent_1rm":
      return `${spec.percent}% от 1ПМ`;
    case "rpe":
      return `RPE ${String(spec.value).replace("-", "–")}`;
    case "rir":
      return `RIR ${String(spec.value).replace("-", "–")}`;
    case "absolute":
      return `${spec.weight_kg} кг`;
    case "free":
      return "";
  }
}

/**
 * «8 × 8–12», «4 × 12-8-8» для дропсета.
 * Тире между повторами — типографское, а не дефис: это читается с расстояния.
 */
export function setsLabel(e: ProgramExercise): string {
  if (e.rep_sequence?.length) {
    return `${e.target_sets} × ${e.rep_sequence.join("-")}`;
  }
  if (e.target_reps_min == null) return `${e.target_sets ?? "?"} подх.`;
  const reps =
    e.target_reps_max && e.target_reps_max !== e.target_reps_min
      ? `${e.target_reps_min}–${e.target_reps_max}`
      : `${e.target_reps_min}`;
  return `${e.target_sets} × ${reps}`;
}

/**
 * Форма слова под число, без самого числа.
 *
 * Нужна там, где число уже напечатано отдельно — например крупной плиткой в
 * профиле. Через plural() там выходило «33 тренировки».
 */
export function wordFor(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** «13 блоков · 21 тренировка · 122 упражнения» с правильными окончаниями. */
export const plural = (n: number, one: string, few: string, many: string): string =>
  `${n} ${wordFor(n, one, few, many)}`;

/** «12 сентября» — дата тренировки в списке людей. Год не нужен: смотрят свежее. */
export const shortDay = (value: string): string =>
  new Date(value).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });

/** Состояние ученика словом. Считает его сервер — правило должно быть одно. */
export const ATHLETE_STATE: Record<string, { label: string; tone: "ok" | "warn" | "bad" }> = {
  active: { label: "ходит", tone: "ok" },
  idle: { label: "пропал", tone: "bad" },
  stalled: { label: "застой", tone: "warn" },
  sick: { label: "болеет", tone: "warn" },
};
